(() => {
  'use strict';

  if (customElements.get('product-media-gallery')) return;

  class ProductMediaGallery extends HTMLElement {
    constructor() {
      super();
      this._listeners = [];
      this._activeMediaId = '';
      this._lightboxIndex = 0;
      this._lightboxZoom = 1;
      this._lightboxPan = { x: 0, y: 0 };
      this._lightboxPanning = false;
      this._lightboxPanMoved = false;
      this._lightboxPanStart = { x: 0, y: 0 };
      this._lightboxPanOrigin = { x: 0, y: 0 };
      this._lightboxToggled = false;
    }

    connectedCallback() {
      // Make re-initialization idempotent for Shopify section reloads.
      this._removeListeners();

      this.main = this.querySelector('[data-gallery-main]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-thumbnail]'));
      this.thumbnailsTrack = this.querySelector('[data-thumbnails]');
      this.thumbnailPrev = this.querySelector('[data-thumbnails-prev]');
      this.thumbnailNext = this.querySelector('[data-thumbnails-next]');

      const scope = this.closest('section, .shopify-section, [data-section]') || this;
      this.lightbox = scope.querySelector('[data-lightbox]') || this.querySelector('[data-lightbox]');
      this.lightboxMedia = this.lightbox ? this.lightbox.querySelector('[data-lightbox-media]') : null;
      this.lightboxImg = this.lightbox ? this.lightbox.querySelector('[data-lightbox-image]') : null;
      this.lightboxThumbnailsTrack = this.lightbox ? this.lightbox.querySelector('[data-lightbox-thumbnails]') : null;
      this.lightboxThumbnails = this.lightbox ? Array.from(this.lightbox.querySelectorAll('[data-thumbnail]')) : [];
      this.lightboxThumbnailsPrev = this.lightbox ? this.lightbox.querySelector('[data-lightbox-thumbnails-prev]') : null;
      this.lightboxThumbnailsNext = this.lightbox ? this.lightbox.querySelector('[data-lightbox-thumbnails-next]') : null;
      if (this.lightbox && !this.lightboxImg && this.lightboxMedia) {
        this.lightboxImg = this._createLightboxImage();
        this.lightboxMedia.appendChild(this.lightboxImg);
      }

      this.enableZoom = this.dataset.enableZoom === 'true';
      this.enableLightbox = this.dataset.enableLightbox === 'true';

      if (!this.main || this.slides.length === 0) return;

      this._bindThumbnails();
      this._bindCarousel();
      this._bindThumbnailDrag();

      if (this.slides.length > 1) {
        this._bindSwipe();
        this._bindKeyboard();
      }

      if (this.enableLightbox && this.lightbox) {
        this._bindLightbox();
      }

      document.addEventListener('sebella:variant-change', this._onVariantChange);
      this._listeners.push({
        element: document,
        type: 'sebella:variant-change',
        handler: this._onVariantChange,
      });
    }

    disconnectedCallback() {
      this._removeListeners();
    }

    _removeListeners() {
      this._listeners.forEach(({ element, type, handler }) => {
        if (element && typeof element.removeEventListener === 'function') {
          element.removeEventListener(type, handler);
        }
      });
      this._listeners = [];

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }

      if (this._lightboxResizeObserver) {
        this._lightboxResizeObserver.disconnect();
        this._lightboxResizeObserver = null;
      }
    }

    _onVariantChange = (event) => {
      if (!event.detail || !event.detail.variant) return;
      const featuredMediaId = event.detail.variant.featured_media_id;
      if (!featuredMediaId) return;
      this.setActiveSlide(String(featuredMediaId), { fromVariant: true });
    };

    get activeMediaId() {
      return this._activeMediaId;
    }

    setActiveSlide(targetId, options = {}) {
      const nextSlide = this.slides.find((slide) => String(slide.dataset.mediaId) === targetId);
      if (!nextSlide) return;

      const activeSlide = this.slides.find((slide) => slide.classList.contains('is-active'));
      const activeIndex = this.slides.indexOf(activeSlide);
      const nextIndex = this.slides.indexOf(nextSlide);
      const direction = nextIndex - activeIndex;

      if (activeSlide === nextSlide) {
        this._scrollToActiveThumbnail(0);
        return;
      }

      this._activeMediaId = targetId;

      if (activeSlide) {
        activeSlide.classList.remove('is-active');
        activeSlide.setAttribute('aria-hidden', 'true');
      }

      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');

      // Sync active thumbnail and scroll it into view.
      this.thumbnails.forEach((thumb) => {
        const isActive = String(thumb.dataset.targetMediaId) === targetId;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      // Sync lightbox thumbnails when the modal is open.
      if (this._isLightboxOpen()) {
        this.lightboxThumbnails.forEach((thumb) => {
          const isActive = String(thumb.dataset.targetMediaId) === targetId;
          thumb.classList.toggle('is-active', isActive);
        });
      }

      this._scrollToActiveThumbnail(direction);
      this._updateCarouselArrows();

      if (this._isLightboxOpen() && this.lightboxImg) {
        const fullImage = nextSlide.dataset.fullImage || nextSlide.dataset.mediaFull;
        if (fullImage) this.lightboxImg.src = fullImage;
        this._lightboxIndex = this.slides.indexOf(nextSlide);
        this._updateLightboxThumbnailArrows();
        this._updateLightboxMeta(direction);
      }

      this.dispatchEvent(
        new CustomEvent('sebella:media-change', {
          bubbles: true,
          detail: {
            mediaId: targetId,
            source: options.fromVariant ? 'variant' : 'user',
            gallery: this,
          },
        })
      );
    }

    _bindThumbnails() {
      if (this.thumbnails.length === 0) return;

      const handler = (event) => {
        if (this._thumbnailDragged) return;
        const thumbnail = event.target.closest('[data-thumbnail]');
        if (!thumbnail) return;
        event.preventDefault();
        this.setActiveSlide(String(thumbnail.dataset.targetMediaId), { fromThumbnail: true });
      };

      this.addEventListener('click', handler);
      this._listeners.push({ element: this, type: 'click', handler });

      // Also bind the lightbox thumbnail track if it lives outside the custom element.
      if (this.lightbox) {
        this.lightbox.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('[data-thumbnail]');
          if (!thumbnail) return;
          event.preventDefault();
          event.stopPropagation();
          this.setActiveSlide(String(thumbnail.dataset.targetMediaId), { fromLightboxThumbnail: true });
        });
        this._listeners.push({ element: this.lightbox, type: 'click', handler });
      }
    }

    _bindThumbnailDrag() {
      if (this.thumbnailsTrack && this.thumbnails.length > 1) {
        this._bindDragOnTrack(this.thumbnailsTrack);
      }
      if (this.lightboxThumbnailsTrack && this.lightboxThumbnails.length > 1) {
        this._bindDragOnTrack(this.lightboxThumbnailsTrack);
      }
    }

    _bindDragOnTrack(track) {
      if (!track) return;

      let startX = 0;
      let startY = 0;
      let startScroll = 0;
      let isDragging = false;
      let dragStarted = false;

      const isVertical = () => this._isVertical(track);
      const canScroll = () => this._thumbnailsTrackHasOverflow(track);

      const start = (event) => {
        if (!canScroll()) return;
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        startScroll = isVertical() ? track.scrollTop : track.scrollLeft;
        isDragging = true;
        dragStarted = false;
      };

      const move = (event) => {
        if (!isDragging) return;
        const touch = event.touches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;

        if (isVertical()) {
          if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 4) {
            event.preventDefault();
            track.scrollTop = startScroll - deltaY;
            dragStarted = true;
          }
        } else {
          if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 4) {
            event.preventDefault();
            track.scrollLeft = startScroll - deltaX;
            dragStarted = true;
          }
        }
      };

      const end = () => {
        isDragging = false;
        if (dragStarted) {
          this._thumbnailDragged = true;
          setTimeout(() => { this._thumbnailDragged = false; }, 50);
        }
        dragStarted = false;
      };

      track.addEventListener('touchstart', start, { passive: true });
      track.addEventListener('touchmove', move, { passive: false });
      track.addEventListener('touchend', end);
      this._listeners.push(
        { element: track, type: 'touchstart', handler: start },
        { element: track, type: 'touchmove', handler: move },
        { element: track, type: 'touchend', handler: end }
      );
    }

    _bindCarousel() {
      if (!this.thumbnailsTrack) {
        this.visibleCount = 5;
        return;
      }

      this.visibleCount = Math.max(1, parseInt(this.thumbnailsTrack.dataset.thumbnailsVisible || 5, 10));

      if (!this.thumbnailPrev && !this.thumbnailNext) return;

      if (this.thumbnailPrev) {
        const prevHandler = (event) => {
          event.preventDefault();
          this._navigateMedia(-1);
        };
        this.thumbnailPrev.addEventListener('click', prevHandler);
        this._listeners.push({ element: this.thumbnailPrev, type: 'click', handler: prevHandler });
      }

      if (this.thumbnailNext) {
        const nextHandler = (event) => {
          event.preventDefault();
          this._navigateMedia(1);
        };
        this.thumbnailNext.addEventListener('click', nextHandler);
        this._listeners.push({ element: this.thumbnailNext, type: 'click', handler: nextHandler });
      }

      this._updateCarouselArrows();

      if (this.thumbnailsTrack && typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => this._updateCarouselArrows());
        this._resizeObserver.observe(this.thumbnailsTrack);
      }
    }

    _navigateMedia(direction) {
      if (!this.slides || this.slides.length === 0) return;
      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (activeIndex === -1) return;

      const nextIndex = activeIndex + direction;
      if (nextIndex < 0 || nextIndex >= this.slides.length) return;

      this.setActiveSlide(String(this.slides[nextIndex].dataset.mediaId), { fromArrow: true });
    }

    _updateCarouselArrows() {
      if (!this.slides || this.slides.length === 0) return;
      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (activeIndex === -1) return;

      const hasOverflow = this._thumbnailsTrackHasOverflow(this.thumbnailsTrack);

      if (this.thumbnailPrev) {
        this.thumbnailPrev.hidden = !hasOverflow;
        this.thumbnailPrev.disabled = activeIndex === 0;
      }
      if (this.thumbnailNext) {
        this.thumbnailNext.hidden = !hasOverflow;
        this.thumbnailNext.disabled = activeIndex === this.slides.length - 1;
      }
    }

    _thumbnailsTrackHasOverflow(track = this.thumbnailsTrack) {
      if (!track) return false;
      const tolerance = 2;
      return (
        track.scrollWidth > track.clientWidth + tolerance ||
        track.scrollHeight > track.clientHeight + tolerance
      );
    }

    _isVertical(track = this.thumbnailsTrack) {
      if (!track) return false;
      const style = window.getComputedStyle(track);
      return style.flexDirection === 'column' || style.overflowY === 'auto' || style.overflowY === 'scroll';
    }

    _scrollToActiveThumbnail(direction = 0) {
      const activeThumb = this.thumbnails.find((thumb) => thumb.classList.contains('is-active'));
      if (!activeThumb) return;
      this._scrollToThumbnail(activeThumb, direction);
    }

    _scrollToThumbnail(thumbnail, direction = 0, track = this.thumbnailsTrack, thumbnails = this.thumbnails) {
      if (!thumbnail || !track || thumbnails.length < 2) return;

      const thumbIndex = thumbnails.indexOf(thumbnail);
      if (thumbIndex === -1) return;

      const isVertical = this._isVertical(track);
      const trackSize = isVertical ? track.clientHeight : track.clientWidth;
      const contentSize = isVertical ? track.scrollHeight : track.scrollWidth;
      const maxScroll = Math.max(0, contentSize - trackSize);
      if (maxScroll === 0) return;

      const thumbSize = isVertical ? thumbnail.offsetHeight : thumbnail.offsetWidth;
      const thumbStep =
        thumbnails.length > 1
          ? isVertical
            ? thumbnails[1].offsetTop - thumbnails[0].offsetTop
            : thumbnails[1].offsetLeft - thumbnails[0].offsetLeft
          : thumbSize;
      if (thumbStep <= 0) return;

      const gap = thumbStep - thumbSize;
      const visibleCount = Math.max(1, Math.floor((trackSize + gap) / thumbStep));
      const currentScroll = isVertical ? track.scrollTop : track.scrollLeft;
      const firstVisible = Math.round(currentScroll / thumbStep);
      const lastVisible = firstVisible + visibleCount - 1;

      let targetStart = -1;

      if (direction > 0 && thumbIndex >= lastVisible) {
        // Moving forward: shift the viewport one thumbnail to the right,
        // keeping the new active image one slot from the right edge.
        targetStart = thumbIndex - (visibleCount - 2);
      } else if (direction < 0 && thumbIndex <= firstVisible) {
        // Moving backward: shift the viewport one thumbnail to the left,
        // keeping the new active image one slot from the left edge.
        targetStart = thumbIndex - 1;
      } else if (direction === 0 && !this._isThumbnailVisible(thumbnail, track)) {
        // No explicit direction (e.g. variant change, initial load): bring it
        // into view with the minimum shift, favoring the center of the strip.
        if (thumbIndex > lastVisible) {
          targetStart = thumbIndex - (visibleCount - 2);
        } else if (thumbIndex < firstVisible) {
          targetStart = thumbIndex - 1;
        }
      }

      if (targetStart === -1) return;

      const targetScroll = Math.max(0, Math.min(targetStart * thumbStep, maxScroll));
      track.scrollTo({
        [isVertical ? 'top' : 'left']: targetScroll,
        behavior: 'smooth',
      });
    }

    _isThumbnailVisible(thumbnail, track = this.thumbnailsTrack) {
      if (!track) return false;

      const trackRect = track.getBoundingClientRect();
      const thumbRect = thumbnail.getBoundingClientRect();
      const isVertical = this._isVertical(track);
      const tolerance = 2;

      if (isVertical) {
        return thumbRect.top + tolerance >= trackRect.top && thumbRect.bottom - tolerance <= trackRect.bottom;
      }

      return thumbRect.left + tolerance >= trackRect.left && thumbRect.right - tolerance <= trackRect.right;
    }

    _bindSwipe() {
      let startX = 0;
      let startY = 0;
      let startTime = 0;

      const start = (event) => {
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        startTime = Date.now();
      };

      const end = (event) => {
        const touch = event.changedTouches[0];
        const diffX = touch.clientX - startX;
        const diffY = touch.clientY - startY;
        const time = Date.now() - startTime;

        if (
          Math.abs(diffX) > Math.abs(diffY) &&
          Math.abs(diffX) > 40 &&
          time < 500
        ) {
          const activeIndex = this.slides.findIndex((slide) =>
            slide.classList.contains('is-active')
          );
          if (activeIndex === -1) return;

          let nextIndex = activeIndex;
          if (diffX < 0 && activeIndex < this.slides.length - 1) nextIndex = activeIndex + 1;
          if (diffX > 0 && activeIndex > 0) nextIndex = activeIndex - 1;

          if (nextIndex !== activeIndex) {
            this.setActiveSlide(String(this.slides[nextIndex].dataset.mediaId), { fromSwipe: true });
          }
        }

        startX = 0;
        startY = 0;
        startTime = 0;
      };

      this.main.addEventListener('touchstart', start, { passive: true });
      this.main.addEventListener('touchend', end, { passive: true });
      this._listeners.push(
        { element: this.main, type: 'touchstart', handler: start },
        { element: this.main, type: 'touchend', handler: end }
      );
    }

    _bindKeyboard() {
      const handler = (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (activeIndex === -1) return;

        let nextIndex = activeIndex;
        if (event.key === 'ArrowRight' && activeIndex < this.slides.length - 1) nextIndex = activeIndex + 1;
        if (event.key === 'ArrowLeft' && activeIndex > 0) nextIndex = activeIndex - 1;

        if (nextIndex !== activeIndex) {
          this.setActiveSlide(String(this.slides[nextIndex].dataset.mediaId), { fromKeyboard: true });
        }
      };

      this.main.setAttribute('tabindex', '0');
      this.main.setAttribute('role', 'region');
      this.main.setAttribute('aria-label', this.getAttribute('aria-label'));
      this.main.addEventListener('keydown', handler);
      this._listeners.push({ element: this.main, type: 'keydown', handler });
    }

    _bindLightbox() {
      const openHandler = (event) => {
        const thumbnail = event.target.closest('[data-thumbnail]');
        if (thumbnail) return;

        const slide = event.target.closest('[data-gallery-slide]');
        if (!slide || !slide.classList.contains('is-active')) return;

        const fullImage = slide.dataset.fullImage || slide.dataset.mediaFull;
        if (!fullImage) return;

        this.openLightbox(fullImage, slide);
      };

      this.main.addEventListener('click', openHandler);
      this._listeners.push({ element: this.main, type: 'click', handler: openHandler });

      if (this.lightbox) {
        const clickHandler = (event) => {
          if (event.target.closest('[data-lightbox-close]')) {
            this.closeLightbox();
            return;
          }

          if (event.target.closest('[data-lightbox-thumbnails-prev]')) {
            this._shiftLightbox(-1);
            return;
          }

          if (event.target.closest('[data-lightbox-thumbnails-next]')) {
            this._shiftLightbox(1);
            return;
          }

          if (event.target.closest('[data-lightbox-image]')) {
            if (this._lightboxToggled) {
              this._lightboxToggled = false;
              this._lightboxPanMoved = false;
              return;
            }
            if (!this._lightboxPanMoved) this._toggleLightboxZoom();
            this._lightboxPanMoved = false;
            return;
          }

          if (event.target === this.lightbox) {
            this.closeLightbox();
          }
        };

        this.lightbox.addEventListener('click', clickHandler);
        this._listeners.push({ element: this.lightbox, type: 'click', handler: clickHandler });

        this._bindLightboxZoom();

        if (this.lightboxThumbnailsTrack) {
          const lightboxThumbnailScrollHandler = () => this._updateLightboxThumbnailArrows();
          this.lightboxThumbnailsTrack.addEventListener('scroll', lightboxThumbnailScrollHandler);
          this._listeners.push({ element: this.lightboxThumbnailsTrack, type: 'scroll', handler: lightboxThumbnailScrollHandler });

          if (typeof ResizeObserver !== 'undefined') {
            this._lightboxResizeObserver = new ResizeObserver(() => this._updateLightboxThumbnailArrows());
            this._lightboxResizeObserver.observe(this.lightboxThumbnailsTrack);
          }
        }

        if (this.lightbox.tagName.toLowerCase() === 'dialog') {
          const cancelHandler = () => this.closeLightbox();
          const closeHandler = () => this.closeLightbox();

          this.lightbox.addEventListener('cancel', cancelHandler);
          this.lightbox.addEventListener('close', closeHandler);
          this._listeners.push(
            { element: this.lightbox, type: 'cancel', handler: cancelHandler },
            { element: this.lightbox, type: 'close', handler: closeHandler }
          );
        }

        const lightboxKeyHandler = (event) => {
          if (!this._isLightboxOpen()) return;

          if (event.key === 'Escape') {
            this.closeLightbox();
            return;
          }

          if (this.slides.length > 1) {
            if (event.key === 'ArrowLeft') {
              this._shiftLightbox(-1);
            } else if (event.key === 'ArrowRight') {
              this._shiftLightbox(1);
            }
          }
        };

        this.lightbox.addEventListener('keydown', lightboxKeyHandler);
        this._listeners.push({ element: this.lightbox, type: 'keydown', handler: lightboxKeyHandler });
      }
    }

    _isLightboxOpen() {
      if (!this.lightbox) return false;
      if (this.lightbox.tagName.toLowerCase() === 'dialog') {
        return this.lightbox.open;
      }
      return this.lightbox.classList.contains('is-open');
    }

    _createLightboxImage() {
      const img = document.createElement('img');
      img.setAttribute('data-lightbox-image', '');
      img.setAttribute('loading', 'lazy');
      img.classList.add('product-media-modal__image', 'product-main__lightbox-image');
      return img;
    }

    _bindLightboxZoom() {
      if (!this.lightboxImg) return;

      this._onLightboxPointerDown = this._onLightboxPointerDown.bind(this);
      this._onLightboxPointerMove = this._onLightboxPointerMove.bind(this);
      this._onLightboxPointerUp = this._onLightboxPointerUp.bind(this);

      this.lightboxImg.addEventListener('pointerdown', this._onLightboxPointerDown);
      this._listeners.push({ element: this.lightboxImg, type: 'pointerdown', handler: this._onLightboxPointerDown });
    }

    _onLightboxPointerDown(event) {
      if (event.button !== 0) return;
      this._lightboxPanning = true;
      this._lightboxPanMoved = false;
      this._lightboxPanStart = { x: event.clientX, y: event.clientY };
      this._lightboxPanOrigin = { ...this._lightboxPan };
      this.lightboxImg.setPointerCapture(event.pointerId);
      this.lightboxImg.addEventListener('pointermove', this._onLightboxPointerMove);
      this.lightboxImg.addEventListener('pointerup', this._onLightboxPointerUp);
      this.lightboxImg.addEventListener('pointercancel', this._onLightboxPointerUp);
      event.preventDefault();
    }

    _onLightboxPointerMove(event) {
      if (!this._lightboxPanning || this._lightboxZoom === 1) return;
      const dx = event.clientX - this._lightboxPanStart.x;
      const dy = event.clientY - this._lightboxPanStart.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this._lightboxPanMoved = true;
      this._lightboxPan = {
        x: this._lightboxPanOrigin.x + dx,
        y: this._lightboxPanOrigin.y + dy,
      };
      this._clampAndApplyLightboxZoom();
    }

    _onLightboxPointerUp(event) {
      if (!this._lightboxPanning) return;
      this._lightboxPanning = false;
      if (this.lightboxImg.hasPointerCapture(event.pointerId)) {
        this.lightboxImg.releasePointerCapture(event.pointerId);
      }
      this.lightboxImg.removeEventListener('pointermove', this._onLightboxPointerMove);
      this.lightboxImg.removeEventListener('pointerup', this._onLightboxPointerUp);
      this.lightboxImg.removeEventListener('pointercancel', this._onLightboxPointerUp);

      if (!this._lightboxPanMoved) {
        this._lightboxToggled = true;
        this._toggleLightboxZoom();
      }
    }

    _toggleLightboxZoom() {
      this._lightboxZoom = this._lightboxZoom === 1 ? 2 : 1;
      this._lightboxPan = { x: 0, y: 0 };
      this._clampAndApplyLightboxZoom();
    }

    _clampAndApplyLightboxZoom() {
      if (!this.lightboxImg || !this.lightboxMedia) return;

      const zoom = this._lightboxZoom;
      const stageW = this.lightboxMedia.clientWidth;
      const stageH = this.lightboxMedia.clientHeight;
      const imgW = this.lightboxImg.offsetWidth * zoom;
      const imgH = this.lightboxImg.offsetHeight * zoom;

      const maxX = Math.max(0, (imgW - stageW) / 2);
      const maxY = Math.max(0, (imgH - stageH) / 2);

      this._lightboxPan.x = Math.max(-maxX, Math.min(maxX, this._lightboxPan.x));
      this._lightboxPan.y = Math.max(-maxY, Math.min(maxY, this._lightboxPan.y));

      this.lightboxImg.style.transform = `translate(${this._lightboxPan.x}px, ${this._lightboxPan.y}px) scale(${zoom})`;
      this.lightboxImg.style.cursor = zoom > 1 ? 'grab' : 'zoom-in';
      this._updateLightboxZoomText();
    }

    _updateLightboxZoomText() {
      const zoomEl = this.lightbox ? this.lightbox.querySelector('[data-lightbox-zoom]') : null;
      if (zoomEl) zoomEl.textContent = `${this._lightboxZoom * 100}%`;
    }

    _resetLightboxZoom() {
      this._lightboxZoom = 1;
      this._lightboxPan = { x: 0, y: 0 };
      if (this.lightboxImg) {
        this.lightboxImg.style.transform = '';
        this.lightboxImg.style.cursor = '';
      }
      this._updateLightboxZoomText();
    }

    _shiftLightbox(delta) {
      if (!this.lightbox || this.slides.length === 0) return;
      const currentIndex = this._lightboxIndex;
      const nextIndex = currentIndex + delta;
      if (nextIndex < 0 || nextIndex >= this.slides.length) return;
      this._showLightboxSlide(nextIndex);
    }

    _showLightboxSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;

      this.setActiveSlide(String(slide.dataset.mediaId), { fromLightbox: true });
    }

    _updateLightboxThumbnailArrows() {
      if (!this.lightbox) return;
      const prev = this.lightbox.querySelector('[data-lightbox-thumbnails-prev]');
      const next = this.lightbox.querySelector('[data-lightbox-thumbnails-next]');
      if (!prev && !next) return;

      const track = this.lightboxThumbnailsTrack;
      if (!track) return;

      const activeIndex = this._lightboxIndex;
      const hasOverflow = this._thumbnailsTrackHasOverflow(track);

      if (prev) {
        prev.hidden = !hasOverflow;
        prev.disabled = activeIndex <= 0;
      }
      if (next) {
        next.hidden = !hasOverflow;
        next.disabled = activeIndex >= this.slides.length - 1;
      }
    }

    _updateLightboxMeta(direction = 0) {
      if (!this.lightbox || this.slides.length === 0) return;

      const slide = this.slides[this._lightboxIndex];
      if (!slide) return;

      const img = slide.querySelector('img');
      const alt = (img && img.alt) ? img.alt : (slide.dataset.mediaAlt || '');
      const productTitle = this.lightbox.dataset.productTitle || '';
      const itemName = alt || productTitle;

      const titleEl = this.lightbox.querySelector('[data-lightbox-item-name]');
      if (titleEl) titleEl.textContent = itemName;

      const counterEl = this.lightbox.querySelector('[data-lightbox-counter]');
      if (counterEl) counterEl.textContent = `${this._lightboxIndex + 1} / ${this.slides.length}`;

      this._resetLightboxZoom();

      const activeThumb = this.lightbox.querySelector(`[data-thumbnail][data-target-media-id="${slide.dataset.mediaId}"]`);
      if (activeThumb && this.lightboxThumbnailsTrack) {
        this._scrollToThumbnail(activeThumb, direction, this.lightboxThumbnailsTrack, this.lightboxThumbnails);
      }
    }

    openLightbox(src, slide) {
      if (!this.lightbox) return;

      if (!this.lightboxImg && this.lightboxMedia) {
        this.lightboxImg = this._createLightboxImage();
        this.lightboxMedia.appendChild(this.lightboxImg);
        this._bindLightboxZoom();
      }

      if (!this.lightboxImg) return;

      const img = slide ? slide.querySelector('img') : null;
      this.lightboxImg.src = src;
      this.lightboxImg.alt = (img && img.alt) ? img.alt : (slide && slide.dataset.mediaAlt ? slide.dataset.mediaAlt : '');

      const activeIndex = this.slides.findIndex((s) => s.classList.contains('is-active'));
      this._lightboxIndex = activeIndex >= 0 ? activeIndex : 0;

      const activeSlide = this.slides[this._lightboxIndex];
      if (activeSlide) {
        this.lightboxThumbnails.forEach((thumb) => {
          const isActive = String(thumb.dataset.targetMediaId) === String(activeSlide.dataset.mediaId);
          thumb.classList.toggle('is-active', isActive);
        });
      }

      this._updateLightboxThumbnailArrows();
      this._updateLightboxMeta(0);

      if (this.lightbox.tagName.toLowerCase() === 'dialog') {
        this.lightbox.removeAttribute('hidden');
        this.lightbox.showModal();
      } else {
        this.lightbox.removeAttribute('hidden');
        this.lightbox.classList.add('is-open');
        this.lightbox.setAttribute('tabindex', '-1');
        this.lightbox.focus();
        document.body.classList.add('modal-open');
      }

      document.body.style.overflow = 'hidden';
    }

    closeLightbox() {
      if (!this.lightbox) return;

      if (this.lightbox.tagName.toLowerCase() === 'dialog') {
        if (this.lightbox.open) this.lightbox.close();
        this.lightbox.setAttribute('hidden', '');
      } else {
        this.lightbox.classList.remove('is-open');
        this.lightbox.setAttribute('hidden', '');
        document.body.classList.remove('modal-open');
      }

      document.body.style.overflow = '';
    }
  }

  customElements.define('product-media-gallery', ProductMediaGallery);
})();
